import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { SelectCustomValue } from '@makyoapp/makyo-common'
import { MoreVert24, Textfield } from '@makyoapp/makyo-icon/icon'
import { SelectDropdown, SimpleInput } from '@makyoapp/makyo-ui'

import { useDebounceInput } from '@/shared/makyo-hooks'

import { RenderColumnCustom } from '../RenderColumn'

interface SelectOrCustomDdProps {
  /**
   * html id
   */
  id: string
  value: SelectCustomValue
  enterCustomPlaceholder?: string
  toggleClassName?: string
  options: { label: string; value: string }[]
  onChange: (value: SelectCustomValue) => void
}

export function SelectOrCustomDd({
  id,
  enterCustomPlaceholder,
  toggleClassName,
  onChange,
  options: rawOptions,
  value: rawValue,
}: SelectOrCustomDdProps) {
  const { t } = useTranslation()

  const enterCustomOption: { label: string; value: string } = {
    value: '#custom#',
    label: enterCustomPlaceholder || t('Enter custom'),
  }

  const options = useMemo(() => {
    return [enterCustomOption, ...rawOptions]
  }, [rawOptions])

  const value = useMemo(() => {
    return rawValue ? options.find((o) => o.value === rawValue.value) || options[0] : options[0]
  }, [options, rawValue])

  const isCustom = useMemo(() => {
    return Boolean(rawValue?.isCustom)
  }, [rawValue])

  const [customVal, cancelCustomVal, setDebCustomVal, val, setCustomVal] = useDebounceInput(
    isCustom && rawValue ? rawValue.value?.toString() : '',
    500
  )

  const handleChange = useCallback(
    (id: string, val?: string) => {
      const isCustom = id === '#custom#'
      onChange({
        isCustom,
        value: isCustom ? (val !== undefined ? val : customVal) : id,
      })
    },
    [onChange, customVal]
  )

  useEffect(() => {
    if (rawValue?.isCustom === true && rawValue?.value !== customVal) {
      handleChange('#custom#', customVal)
    }
  }, [customVal, rawValue, handleChange])

  return (
    <div className='w-full min-w-0'>
      {isCustom ? (
        <div>
          <SimpleInput
            placeholder={t('Enter Custom')}
            value={val}
            onChange={(e) => {
              const val = e.currentTarget.value
              cancelCustomVal()
              setCustomVal(val)
              //handleChange(value, val)
            }}
            inputClassName='min-w-0'
            after={
              <SelectDropdown
                id={id}
                menuDefaultSize={false}
                menuClassName={'max-h-[220px] overflow-y-auto'}
                menuContainerClassName='mk-ag-sdd'
                toggleClassName={clsx(
                  //withCustomValue && 'mk-ag-col-toggle',
                  toggleClassName,
                  'p-0! ltr:pr-[2px]! rtl:pl-[2px]! bg-transparent!'
                )}
                menuHeadingClassName='px-4! py-2 text-light border-t bdr'
                menuZIndex='z-305'
                value={value}
                onChange={(opt) => {
                  handleChange(opt.value)
                }}
                optionLabel={(opt) => opt.value}
                withSearch
                portalMenu
                customArrow={<MoreVert24 width={16} height={16} />}
                renderOption={(opt, inToggle) => {
                  if (inToggle) {
                    return null
                  }
                  if (opt.value === '#custom#') {
                    return <RenderColumnCustom name={opt.label} icon={<Textfield className='fill-primary-500' />} />
                  }
                  return opt.label
                }}
                options={options}
              />
            }
          />
        </div>
      ) : (
        <SelectDropdown
          className='w-full'
          id={id}
          menuDefaultSize={false}
          menuClassName={'max-h-[220px] overflow-y-auto'}
          menuContainerClassName='mk-ag-sdd'
          toggleClassName={clsx(
            //withCustomValue && 'mk-ag-col-toggle',
            toggleClassName
          )}
          menuHeadingClassName='px-4! py-2 text-light border-t bdr'
          menuZIndex='z-305'
          value={value}
          onChange={(opt) => {
            handleChange(opt.value)
          }}
          optionLabel={(opt) => opt.label}
          withSearch
          portalMenu
          customArrow={<MoreVert24 width={16} height={16} />}
          renderOption={(opt) => {
            if (!opt) {
              return null
            }
            if (opt.value === '#custom#') {
              return <RenderColumnCustom name={opt.label} icon={<Textfield className='fill-primary-500' />} />
            }
            return opt.label
          }}
          options={options}
        />
      )}
    </div>
  )
}
